import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ArrowLeft } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import Seo, { ORG_JSON_LD } from "../components/marketing/Seo";

const POSTS = [
  {
    slug: "ai-chatbot-vs-voice-agent-which-is-right",
    title: "AI chatbot vs voice agent: which one should your business deploy first?",
    excerpt: "A 6-minute breakdown of where text wins, where voice wins, and how to decide based on your customers, your budget and your existing tooling.",
    date: "2026-02-18",
    read: "6 min read",
    cover: "linear-gradient(135deg,#FF8A1B 0%,#EC4899 70%,#7C3AED 100%)",
    tag: "Strategy",
    keywords: ["AI chatbot vs voice agent", "voice agent ROI", "AI customer service comparison", "best AI chatbot 2026"],
    body: `Most teams reach for a text chatbot first because the setup feels easy. Voice gets parked as "phase 2." That instinct is right for a SaaS knowledge base — and wrong for almost every business with a phone number on its website.

This guide walks through three lenses to pick your channel: customer expectation, conversion stage, and your team's current bandwidth. We'll also share the math on cost-per-conversation we see across HelloSir.ai's healthcare, real-estate and ecommerce clients.

**TL;DR**
- Text chat wins for SaaS, EdTech, B2B onboarding and any "help me figure this out" flow.
- Voice agent wins for restaurants, clinics, dealerships and high-intent moments where customers are away from a keyboard.
- AI calling (outbound) wins for lead qualification at scale and reactivation campaigns.

The free starter on HelloSir.ai gives you 100 chats AND 20 minutes of voice — train one agent, ship both channels on day one, and let the conversion data decide. No need to commit upfront.`,
  },
  {
    slug: "how-to-train-an-ai-agent-on-your-website",
    title: "How to train an AI agent on your own website in under 10 minutes",
    excerpt: "Step-by-step: pasting your URL, what the crawler actually grabs, why semantic search beats keyword matching, and the 3 prompt rules that stop hallucinations.",
    date: "2026-02-16",
    read: "8 min read",
    cover: "linear-gradient(135deg,#0044FF 0%,#7C3AED 60%,#F97316 100%)",
    tag: "How-to",
    keywords: ["train AI chatbot on website", "RAG chatbot setup", "auto crawl website AI", "AI knowledge base", "hellosir.ai training"],
    body: `Training a sales-ready AI agent on your own content used to mean LangChain glue code, Pinecone setup and a $3,000 invoice from a freelancer. Now it's a paste-a-URL flow.

In this post we'll walk through HelloSir.ai's knowledge-base ingestion end-to-end, what the SPA-aware crawler actually captures, how semantic search routes the right answer back to your customer, and the three prompt rules that keep the model honest:

1. **Ground every answer in the knowledge base** — never let the model freelance pricing.
2. **Refuse outside the scope** — "I don't know — let me connect you with our team."
3. **Lock language and persona** — customers in Dubai get Arabic, customers in Bristol get British English.`,
  },
  {
    slug: "ai-receptionist-for-clinics-real-world-roi",
    title: "AI receptionist for clinics: 3 real-world ROI numbers from HelloSir.ai users",
    excerpt: "From a single-chair dental practice to a 4-location physiotherapy group — anonymised numbers on after-hours bookings captured, repetitive calls deflected and missed-call cost saved.",
    date: "2026-02-12",
    read: "5 min read",
    cover: "linear-gradient(135deg,#06B6D4 0%,#10B981 70%,#F97316 100%)",
    tag: "Healthcare",
    keywords: ["AI receptionist for healthcare clinics", "AI medical chatbot ROI", "AI for dental practice", "AI front desk medical", "healthcare AI case study"],
    body: `The phones at most independent clinics ring loudest between 6pm and 10pm — exactly when reception goes home. We pulled anonymised conversation logs from three clinics using HelloSir.ai over a 60-day window. Here's what we found.

**Clinic A — single-dentist practice, London**
- 38% more new-patient bookings captured after 6pm vs the pre-AI baseline.
- 72% drop in reception calls about opening hours / parking / insurance.
- Net saving: £1,400/month in receptionist overtime.

**Clinic B — 4-location physiotherapy group, Manchester**
- AI handled 2,140 routine enquiries across all 4 sites in 60 days.
- Average response time fell from 6.5 hours (email) → 12 seconds (chat).
- Voice agent took 187 missed-call recoveries that the practice would have lost.

**Clinic C — dental + cosmetic, Dubai**
- Multilingual coverage (English / Arabic / Hindi) increased qualified leads from international patients by 47%.

All three rolled out on the free starter (100 chats + 20 voice minutes) before topping up. None pay more than $80/month today.`,
  },
];

const BLOG_LD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  url: "https://hellosir.ai/blog",
  name: "HelloSir.ai Blog",
  description: "Strategy, how-to and ROI stories about deploying AI text, voice and calling agents in real businesses.",
  publisher: ORG_JSON_LD,
};

/* ----------------- Blog index ----------------- */
export function Blog() {
  return (
    <MarketingLayout>
      <Seo
        title="Blog — AI Agent Strategy, How-tos & Case Studies"
        description="Practical playbooks, ROI numbers and how-tos for deploying AI text, voice and calling agents — by HelloSir.ai, a Cloud Lab Technologies (UK) product."
        keywords={["AI chatbot blog", "AI voice agent blog", "AI customer service case studies", "AI for business how-to", "hellosir.ai blog"]}
        path="/blog"
        jsonLd={BLOG_LD}
      />
      <section className="border-b border-[#E2E8F0] bg-gradient-to-br from-[#FFEFE0] via-white to-[#FFE4F1]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <span className="text-xs font-mono uppercase tracking-widest text-[#F97316]">// blog</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-3 leading-[0.95] tracking-tight">
            Strategy, how-tos and real numbers <br />
            <span style={{ background: "linear-gradient(135deg,#F97316 0%,#EC4899 60%,#7C3AED 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>from teams shipping AI agents.</span>
          </h1>
        </div>
      </section>
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {POSTS.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-[6px] border border-[#E2E8F0] bg-white overflow-hidden hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(2,6,23,0.18)] transition-all" data-testid={`blog-card-${p.slug}`}>
              <div className="aspect-[5/3]" style={{ background: p.cover }} />
              <div className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F97316]">{p.tag}</div>
                <h3 className="font-display font-bold text-lg mt-2 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#4B5563] mt-3 leading-relaxed">{p.excerpt}</p>
                <div className="text-[11px] font-mono text-[#4B5563] mt-4 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.read}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}

/* ----------------- Single post ----------------- */
export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const ARTICLE_LD = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { "@type": "Organization", name: "HelloSir.ai" },
    publisher: ORG_JSON_LD,
    mainEntityOfPage: `https://hellosir.ai/blog/${post.slug}`,
  };

  return (
    <MarketingLayout>
      <Seo
        title={post.title}
        description={post.excerpt}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        jsonLd={ARTICLE_LD}
      />
      <section className="border-b border-[#E2E8F0]">
        <div className="aspect-[16/5]" style={{ background: post.cover }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <Link to="/blog" className="text-sm text-[#F97316] inline-flex items-center gap-1.5 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F97316] mt-6">{post.tag}</div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 leading-tight">{post.title}</h1>
          <div className="text-[11px] font-mono text-[#4B5563] mt-4 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{post.read}</span>
          </div>
        </div>
      </section>
      <section className="py-12 lg:py-16">
        <article className="max-w-3xl mx-auto px-6 lg:px-10 prose prose-slate prose-headings:font-display prose-headings:font-bold prose-p:text-[#374151] prose-p:leading-relaxed prose-a:text-[#F97316] hover:prose-a:underline">
          {post.body.split("\n\n").map((para, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br/>") }} />
          ))}
          <div className="mt-12 p-6 rounded-[6px]" style={{ background: "linear-gradient(135deg,#FFEFE0 0%,#FFE4F1 100%)" }}>
            <div className="font-display font-bold text-xl">Try the free starter — no card needed.</div>
            <p className="text-sm text-[#4B5563] mt-2">100 web chats + 20 minutes of web voice. Train an agent on your site in under 10 minutes.</p>
            <Link to="/signup" className="btn-primary text-sm inline-flex items-center gap-2 mt-4">
              Start free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </article>
      </section>
    </MarketingLayout>
  );
}

export const BLOG_POSTS = POSTS;
